import { type ReactNode, useState } from 'react';
import { App as AntdApp, ConfigProvider } from 'antd';
import idID from 'antd/locale/id_ID';
// AntD's locale pack names the dayjs locale but does not contain it; the date
// pickers F3's forms are built on read their month and weekday names straight
// from dayjs. Without this side-effect import the chrome is Indonesian and the
// calendar inside it is English.
import 'dayjs/locale/id';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from './query-client';
import { theme } from './theme';
import { SessionProvider } from '../auth/session';

/**
 * Everything the app needs in context, in one place so tests can mount a screen
 * under the same providers the browser gives it (docs/07-implementation-roadmap.md
 * F0.2, F0.3).
 *
 * The UI is Bahasa Indonesia only and there is no i18n library (docs/07 §0), so
 * AntD's own chrome — date pickers, pagination, table empty states — is set
 * from its `id_ID` locale pack here and nowhere else.
 */

/**
 * `id_ID` ships one string untranslated — `Form.optional`, the marker on an
 * optional field's label. It is **not** patched here: overriding it on this
 * provider works in jsdom and not in the production bundle, so the forms render
 * the marker themselves (`src/components/required-mark.tsx`).
 */

export function AppProviders({
  children,
  client,
  /**
   * Component tests that set the session up themselves skip the boot restore —
   * it would be an unhandled `POST /auth/refresh` in every one of them, and MSW
   * is configured to fail on those rather than let a request escape.
   */
  restoreSession = true,
}: {
  children: ReactNode;
  client?: QueryClient;
  restoreSession?: boolean;
}) {
  // Created once per mount rather than at module scope: a module-level client
  // is shared between tests, and one test's cache then answers another's query.
  const [queryClient] = useState(() => client ?? createQueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider locale={idID} theme={theme}>
        {/* AntD's App supplies the context that `message`/`modal`/`notification`
            need to pick up the theme above; without it they render unthemed. */}
        <AntdApp>
          {/* Inside the query client: signing out clears its cache (docs/07 F1.4). */}
          {restoreSession ? <SessionProvider>{children}</SessionProvider> : children}
        </AntdApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
